<template>
    <div class="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 min-h-screen font-body">
        <header class="bg-orange-600 text-white py-6 shadow-2xl">
            <div class="container mx-auto px-4">
                <div class="flex flex-col items-center text-center">
                    <div class="flex items-center gap-4 mb-2">
                        <i class="fas fa-utensils text-5xl"></i>
                        <h1 class="text-4xl md:text-5xl font-black">COZINHA - Pedidos</h1>
                    </div>
                    <p class="text-orange-200 text-lg font-semibold">{{ nomeLoja }}</p>
                </div>
            </div>
        </header>

        <main class="container mx-auto px-4 py-8">
            <div v-if="loading" class="flex flex-col items-center justify-center py-16 text-white">
                <svg class="animate-spin h-16 w-16 text-orange-300 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                </svg>
                <span class="text-xl font-bold">Carregando pedidos...</span>
            </div>

            <div v-else>
                <!-- Pedidos em Preparo -->
                <div v-if="pedidosPreparando.length === 0" class="text-center py-16 text-white/60 mb-8">
                    <i class="fas fa-check-circle text-8xl mb-4"></i>
                    <p class="text-2xl font-bold">Nenhum pedido em preparo</p>
                </div>

                <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    <div 
                        v-for="pedido in pedidosPreparando.slice(0, 6)" 
                        :key="pedido.id"
                        :class="[
                            'rounded-2xl shadow-2xl p-6 transform transition-all duration-300 hover:scale-105',
                            pedidoCancelado(pedido) 
                                ? 'bg-gradient-to-br from-red-900 to-red-800 border-4 border-red-500 animate-pulse-fast' 
                                : 'bg-white/10 backdrop-blur-lg border-2 border-amber-400'
                        ]"
                    >
                        <!-- Cabeçalho do Card -->
                        <div class="flex items-center justify-between mb-4 pb-3 border-b-2 border-white/20">
                            <div class="flex items-center gap-3">
                                <div class="bg-amber-500 rounded-lg px-3 py-1">
                                    <p class="text-sm font-bold text-white uppercase tracking-wider">Senha</p>
                                    <p class="text-2xl font-black text-white">{{ pedido.senha }}</p>
                                </div>
                                <div v-if="pedidoCancelado(pedido)" class="bg-red-600 rounded-lg px-3 py-2 animate-bounce">
                                    <p class="text-xs font-black text-white uppercase">CANCELADO</p>
                                </div>
                            </div>
                            <div class="text-right">
                                <p class="text-white/80 text-xs">
                                    <i class="fas fa-user mr-1"></i>{{ pedido.cliente_nome }}
                                </p>
                                <p class="text-white/60 text-xs mt-1">
                                    <i class="fas fa-clock mr-1"></i>{{ formatarTempo(pedido.created_at) }}
                                </p>
                            </div>
                        </div>

                        <!-- Lista de Itens -->
                        <div class="space-y-2 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                            <div 
                                v-for="item in pedido.itens" 
                                :key="item.id"
                                :class="[
                                    'rounded-lg p-3 border-l-4',
                                    item.ativo === 0 
                                        ? 'bg-red-900/30 border-red-500 line-through opacity-60' 
                                        : 'bg-gradient-to-r from-slate-700 to-slate-600 border-green-400'
                                ]"
                            >
                                <div class="flex items-start justify-between gap-3">
                                    <div class="flex-1">
                                        <div class="flex items-center gap-2 mb-1">
                                            <span class="bg-green-500 text-white text-xs font-black px-2 py-1 rounded">
                                                {{ item.quantidade }}x
                                            </span>
                                            <p class="text-white font-bold text-sm">{{ item.produto_nome }}</p>
                                        </div>
                                        <p v-if="item.categoria_nome" class="text-white/60 text-xs ml-8">
                                            <i class="fas fa-tag mr-1"></i>{{ item.categoria_nome }}
                                        </p>
                                        <p v-if="item.ativo === 0" class="text-red-400 text-xs font-bold mt-1 ml-8">
                                            <i class="fas fa-ban mr-1"></i>Item cancelado
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Total do Pedido -->
                        <div class="mt-4 pt-3 border-t-2 border-white/20">
                            <div class="flex items-center justify-between">
                                <span class="text-white/80 text-sm font-semibold">Total do Pedido:</span>
                                <span class="text-white text-lg font-black">{{ formatarValor(pedido.valor_total) }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Últimos Pedidos Finalizados -->
                <div v-if="pedidosFinalizados.length > 0" class="bg-white/5 backdrop-blur-lg rounded-2xl shadow-2xl p-6 border border-white/20">
                    <div class="flex items-center gap-3 mb-4 pb-3 border-b border-white/20">
                        <i class="fas fa-history text-2xl text-blue-300"></i>
                        <h3 class="text-xl font-bold text-white">Últimos Pedidos Finalizados</h3>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        <div 
                            v-for="pedido in pedidosFinalizados" 
                            :key="pedido.id"
                            class="bg-white/5 rounded-lg p-3 border border-green-500/30"
                        >
                            <div class="flex items-center justify-between mb-2">
                                <div class="bg-green-500 rounded px-2 py-1">
                                    <p class="text-sm font-black text-white">{{ pedido.senha }}</p>
                                </div>
                                <i class="fas fa-check-circle text-green-400 text-lg"></i>
                            </div>
                            <p class="text-white/80 text-xs mb-1">
                                <i class="fas fa-user mr-1"></i>{{ pedido.cliente_nome }}
                            </p>
                            <p class="text-white/60 text-xs">
                                <i class="fas fa-clock mr-1"></i>{{ formatarTempo(pedido.created_at) }}
                            </p>
                            <p class="text-white/50 text-xs mt-2">{{ pedido.itens?.length || 0 }} item(ns)</p>
                        </div>
                    </div>
                </div>
            </div>
        </main>

        <footer class="bg-slate-950 text-white py-6 mt-12">
            <div class="container mx-auto px-4 text-center">
                <p class="text-slate-300 text-sm font-semibold">
                    Sistema de Cozinha • Atualização automática a cada 10 segundos
                </p>
            </div>
        </footer>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted, onUnmounted } from 'vue';
    import { useRoute } from 'vue-router';
    import { storeToRefs } from "pinia";
    import { useSenhaStore } from '@/stores/senha';

    const route = useRoute();
    const senhaStore = useSenhaStore();
    const { senhas, nomeLoja } = storeToRefs(senhaStore);

    const loading = ref(true);
    const lojaId = ref(null);
    let intervalId = null;

    // Pedidos em preparo (situacao 0)
    const pedidosPreparando = computed(() => {
        return senhas.value
            .filter(senha => senha.situacao === 0)
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    });

    // Últimos 5 pedidos finalizados (situacao 1)
    const pedidosFinalizados = computed(() => {
        return senhas.value
            .filter(senha => senha.situacao === 1)
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 5);
    });

    // Verifica se o pedido foi cancelado (tem todos os itens com ativo = 0)
    function pedidoCancelado(pedido) {
        if (!pedido.itens || pedido.itens.length === 0) return false;
        return pedido.itens.every(item => item.ativo === 0);
    }

    onMounted(async () => {
        lojaId.value = route.params.lojaId || route.query.lojaId;

        if (!lojaId.value) {
            console.error('lojaId não fornecido');
            loading.value = false;
            return;
        }

        await carregarPedidos();

        // Atualiza a cada 10 segundos
        intervalId = setInterval(carregarPedidos, 10000);
    });

    onUnmounted(() => {
        if (intervalId) {
            clearInterval(intervalId);
        }
    });

    async function carregarPedidos() {
        try {
            await senhaStore.listarSenhasPedido(lojaId.value);
        } catch (error) {
            console.error('Erro ao carregar pedidos:', error);
        } finally {
            loading.value = false;
        }
    }

    function formatarTempo(data) {
        const agora = new Date();
        const dataItem = new Date(data);
        const diffMs = agora - dataItem;
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return 'Agora';
        if (diffMins === 1) return '1 min atrás';
        if (diffMins < 60) return `${diffMins} mins atrás`;

        const diffHours = Math.floor(diffMins / 60);
        if (diffHours === 1) return '1 hora atrás';
        if (diffHours < 24) return `${diffHours} horas atrás`;

        return dataItem.toLocaleDateString('pt-BR');
    }

    function formatarValor(valor) {
        if (!valor && valor !== 0) return 'R$ 0,00';
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor);
    }
</script>

<style scoped>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');

    .font-body {
        font-family: 'Inter', sans-serif;
    }

    .custom-scrollbar::-webkit-scrollbar {
        width: 6px;
    }

    .custom-scrollbar::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 10px;
    }

    .custom-scrollbar::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.3);
        border-radius: 10px;
    }

    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.5);
    }

    .animate-pulse-fast {
        animation: pulse-fast 1s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }

    @keyframes pulse-fast {
        0%, 100% {
            opacity: 1;
        }
        50% {
            opacity: 0.7;
        }
    }
</style>
